import { createApi } from '@reduxjs/toolkit/query/react'
import { track } from '@vercel/analytics'
import { TRACK_EVENT_SECTION_NAME } from '../../commons/constants/constants'
import { captureSentryError } from '../../commons/utils/utils'
import { setAuth, setIsLoggingIn, setUser } from '../slices/authSlice'
import { setError } from '../slices/errorSlice'
import { getBaseQuery } from './apiUtils'
import apiConstants, { HTTP_METHODS } from './constants'

const authBaseParameters = {
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json'
  }
}

export const authApi = createApi({
  reducerPath: 'authApi',
  refetchOnFocus: false,
  baseQuery: getBaseQuery(),
  endpoints: (builder) => ({
    login: builder.mutation<AdminUser, LoginParams>({
      query: (param) => ({
        ...authBaseParameters,
        method: HTTP_METHODS.POST,
        url: apiConstants.AUTH_LOGIN_URL,
        body: JSON.stringify({
          email: param.email,
          password: param.password
        })
      }),
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          if (data && !data.showErrDialog && data.AdminUserID) {
            dispatch(setUser(data))
            dispatch(setAuth(true))
            track(TRACK_EVENT_SECTION_NAME.GENERAL, {
              LoggedAdminUserID: data.AdminUserID,
              Event: 'Sign In'
            })
          }
        } catch (err: any) {
          captureSentryError(err)
          dispatch(setError())
        }
      }
    }),
    forgotPassword: builder.mutation({
      query: (param: Partial<LoginParams>) => ({
        ...authBaseParameters,
        method: HTTP_METHODS.POST,
        url: apiConstants.AUTH_FORGOT_PASSWORD_URL,
        body: JSON.stringify({
          email: param.email
        })
      }),
      async onQueryStarted ({}, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch (err: any) {
          captureSentryError(err)
          dispatch(setError())
        }
      }
    }),
    logout: builder.mutation({
      query: (arg: void) => ({
        ...authBaseParameters,
        method: HTTP_METHODS.GET,
        url: apiConstants.AUTH_LOGOUT_URL
      }),
      async onQueryStarted (arg, { dispatch }) {
        dispatch(setAuth(false))
        dispatch(setIsLoggingIn(false))
      }
    }),
    reportMFARegister: builder.mutation({
      query: (param: ReportMFARegisterParams) => ({
        ...authBaseParameters,
        method: HTTP_METHODS.POST,
        url: apiConstants.AUTH_SETUP_MFA_SET_URL,
        body: JSON.stringify({
          AuthURL: param.AuthURL
        })
      })
    }),
    verifyMFA: builder.mutation<{ success: boolean }, VerifyMFAParams>({
      query: (param) => ({
        ...authBaseParameters,
        method: HTTP_METHODS.POST,
        url: apiConstants.AUTH_VERIFY_MFA_URL,
        body: JSON.stringify({
          AuthURL: param.AuthURL,
          Email: param.Email,
          token: param.token
        })
      })
    })
  })
})

export const {
  useLoginMutation,
  useForgotPasswordMutation,
  useLogoutMutation,
  endpoints,
  useReportMFARegisterMutation,
  useVerifyMFAMutation
} = authApi
